import React from 'react';
import {useTranslation} from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Link,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  Fade,
  Tooltip,
  IconButton,
} from '@mui/material';
import VisaSvg from '../../assets/icons/payment/visa.svg';
import MastercardSvg from '../../assets/icons/payment/mastercard.svg';
import ApplePaySvg from '../../assets/icons/payment/apple-pay.svg';
import GooglePaySvg from '../../assets/icons/payment/google-pay.svg';
import BancontactSvg from '../../assets/icons/payment/bancontact.svg';
import {FaFacebookF, FaYoutube, FaInstagram, FaPinterest, FaGoogle, FaTripadvisor} from 'react-icons/fa';
import {paths} from 'packages/common/routes/paths';

const Footer = ({companyDetails}) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const appName = process.env.REACT_APP_NAME;

  const {backgroundColor, website} = companyDetails || {};

  // Calcul de la couleur du texte
  const textColor = React.useMemo(() => {
    if (!backgroundColor) return '#FFFFFF';
    const color = backgroundColor.charAt(0) === '#' ? backgroundColor.substring(1, 7) : backgroundColor;
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128 ? '#FFFFFF' : '#161616';
  }, [backgroundColor]);

  const activeSocialLinks = React.useMemo(() => {
    const links = [
      {url: companyDetails?.socialMedia?.instagram, icon: FaInstagram, label: 'Instagram', hoverColor: '#E1306C'},
      {url: companyDetails?.socialMedia?.facebook, icon: FaFacebookF, label: 'Facebook', hoverColor: '#4267B2'},
      {url: companyDetails?.socialMedia?.youtube, icon: FaYoutube, label: 'YouTube', hoverColor: '#FF0000'},
      {url: companyDetails?.socialMedia?.pinterest, icon: FaPinterest, label: 'Pinterest', hoverColor: '#E60023'},
      {url: companyDetails?.socialMedia?.googleMyBusiness, icon: FaGoogle, label: 'Google', hoverColor: '#4285F4'},
      {url: companyDetails?.socialMedia?.tripAdvisor, icon: FaTripadvisor, label: 'TripAdvisor', hoverColor: '#00AA6C'},
    ];
    return links.filter((link) => link.url);
  }, [companyDetails?.socialMedia]);

  // Sans réseaux sociaux, la colonne centrale ET tous les séparateurs
  // disparaissent : juste info à gauche, paiement à droite
  const hasSocialLinks = activeSocialLinks.length > 0;

  const paymentMethods = [
    {icon: VisaSvg, name: 'visa'},
    {icon: MastercardSvg, name: 'mastercard'},
    {icon: ApplePaySvg, name: 'applePay'},
    {icon: GooglePaySvg, name: 'googlePay'},
    {icon: BancontactSvg, name: 'bancontact'},
  ];

  return (
    <Fade in timeout={800}>
      <Box
        component="footer"
        sx={{
          'width': '100%',
          'backgroundColor': backgroundColor || '#161616',
          'color': textColor,
          'position': 'relative',
          'mt': 'auto',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, 
              rgba(255,255,255,0) 0%, 
              ${textColor}20 20%, 
              ${textColor}40 50%, 
              ${textColor}20 80%, 
              rgba(255,255,255,0) 100%
            )`,
          },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{py: 4}}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: (() => {
                  if (!isMdUp) return '1fr';
                  return hasSocialLinks ? '1fr 2px 1fr 2px 1fr' : '1fr 1fr';
                })(),
                gap: 2,
                mb: 3,
              }}
            >
              {/* Info entreprise */}
              <Box sx={{textAlign: isMdUp ? 'left' : 'center'}}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1.5,
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                  }}
                >
                  {companyDetails?.name || appName}
                </Typography>
                {website && (
                  <Link
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      'color': 'inherit',
                      'textDecoration': 'none',
                      'display': 'inline-block',
                      'transition': 'transform 0.2s ease-in-out, opacity 0.2s',
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {website}
                  </Link>
                )}
              </Box>

              {isMdUp && hasSocialLinks && <Divider orientation="vertical" flexItem sx={{borderColor: `${textColor}30`}} />}

              {/* Section réseaux sociaux — colonne absente si aucun réseau configuré */}
              {hasSocialLinks && (
                <>
                  <Box sx={{textAlign: 'center'}}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 500,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {t('footer.followUs')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 1.5,
                      }}
                    >
                      {activeSocialLinks.map(({url, icon: Icon, label, hoverColor}) => (
                        <Tooltip key={label} title={label} placement="top" arrow>
                          <IconButton
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{
                              'color': textColor,
                              'transition': 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: `${hoverColor}20`,
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <Icon size={18} />
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>

                  {isMdUp && <Divider orientation="vertical" flexItem sx={{borderColor: `${textColor}30`}} />}
                </>
              )}

              {/* Moyens de paiement - toujours visible */}
              <Box sx={{textAlign: isMdUp ? 'right' : 'center'}}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                  }}
                >
                  {t('footer.securePayment')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: isMdUp ? 'flex-end' : 'center',
                    gap: 1.5,
                    backgroundColor: 'transparent',
                  }}
                >
                  {paymentMethods.map(({icon, name}) => (
                    <Box
                      key={name}
                      component="img"
                      src={icon}
                      alt={t(`footer.paymentMethods.${name}`)}
                      sx={{
                        'height': 24,
                        'filter': 'grayscale(100%)',
                        'opacity': 0.8,
                        'transition': 'all 0.2s ease',
                        '&:hover': {
                          filter: 'grayscale(0%)',
                          opacity: 1,
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  ))}
                </Paper>
              </Box>
            </Box>

            {/* Bas de page */}
            <Box
              sx={{
                pt: 2,
                mt: 2,
                borderTop: `1px solid ${textColor}20`,
                display: 'flex',
                flexDirection: isMdUp ? 'row' : 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.8,
                  letterSpacing: '0.01em',
                }}
              >
                &copy; {new Date().getFullYear()} {t('footer.poweredBy')}{' '}
                <Link
                  href="https://brydge.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    'color': 'inherit',
                    'textDecoration': 'none',
                    'fontWeight': 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {appName}
                </Link>
              </Typography>

              {companyDetails?.slug && (
                <Link
                  href={paths.company.terms.replace(':slug', companyDetails.slug)}
                  variant="body2"
                  sx={{
                    'color': 'inherit',
                    'textDecoration': 'none',
                    'opacity': 0.8,
                    'letterSpacing': '0.01em',
                    'transition': 'all 0.2s ease',
                    '&:hover': {
                      opacity: 1,
                      transform: 'translateX(2px)',
                    },
                  }}
                >
                  {t('footer.terms')}
                </Link>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Fade>
  );
};

export default Footer;
